import React, { useState } from 'react';
import { SafeImage } from '../SafeImage';
import { ImagePickerModal, PRESET_IMAGES } from './ImagePickerModal';
import { Upload, FolderOpen, Image as ImageIcon, Sparkles, Check, RefreshCw } from 'lucide-react';

interface ImageSelectorFieldProps {
  value: string;
  onChange: (url: string) => void;
  label?: string;
  description?: string;
  categoryHint?: 'equipment' | 'blog' | 'general';
}

export const ImageSelectorField: React.FC<ImageSelectorFieldProps> = ({
  value,
  onChange,
  label = 'Asset Image / Photo',
  description = 'Upload a real equipment/story photo or select from library and factory presets.',
  categoryHint = 'equipment'
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isDataUrl = value?.startsWith('data:');
  const isExternalUrl = value?.startsWith('http://') || value?.startsWith('https://');

  const activePreset = PRESET_IMAGES.find((p) => p.url === value);
  const relevantPresets = PRESET_IMAGES.filter((p) =>
    categoryHint === 'blog' ? p.category === 'blog' : p.category === 'equipment'
  ).slice(0, 4);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-xs font-semibold text-slate-300">
          {label}
        </label>
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="text-xs text-sky-400 hover:text-sky-300 font-semibold flex items-center gap-1 cursor-pointer"
        >
          <Upload className="w-3.5 h-3.5" />
          <span>Upload or Browse Media</span>
        </button>
      </div>

      {/* Main Selected Image Card */}
      <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 flex flex-col sm:flex-row items-center gap-4">
        <div className="relative w-full sm:w-36 aspect-video sm:aspect-4/3 rounded-lg overflow-hidden bg-slate-900 border border-slate-800 shrink-0">
          <SafeImage
            src={value}
            fallbackCategory={categoryHint === 'blog' ? 'blog' : 'equipment'}
            alt="Current image asset"
            className="w-full h-full object-cover"
          />
          <div className="absolute top-1.5 left-1.5">
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-black/70 backdrop-blur-xs text-white">
              {isDataUrl ? 'Uploaded File' : isExternalUrl ? 'Web URL' : activePreset ? activePreset.badge : 'Selected Asset'}
            </span>
          </div>
        </div>

        <div className="flex-1 min-w-0 space-y-2 w-full">
          <div>
            <div className="text-[11px] font-mono text-slate-400 truncate">
              {isDataUrl ? 'Local / Server Uploaded Image (Base64 optimized)' : value}
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {description}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="px-3.5 py-1.5 bg-sky-600 hover:bg-sky-500 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-sky-600/20 cursor-pointer transition-colors"
            >
              <Upload className="w-3.5 h-3.5" />
              <span>Upload / Select Image</span>
            </button>

            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="px-3 py-1.5 bg-slate-900 hover:bg-slate-850 border border-slate-750 text-slate-300 hover:text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <FolderOpen className="w-3.5 h-3.5 text-sky-400" />
              <span>Media Library</span>
            </button>
          </div>
        </div>
      </div>

      {/* Quick 1-Click Preset Shortcuts */}
      <div className="pt-1">
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-sky-400" />
          <span>Quick Preset Shortcuts:</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {relevantPresets.map((preset) => {
            const isMatch = value === preset.url;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => onChange(preset.url)}
                className={`p-1.5 rounded-lg border text-left flex items-center gap-2 transition-all cursor-pointer ${
                  isMatch
                    ? 'border-sky-500 bg-sky-950/40 text-white'
                    : 'border-slate-800 bg-slate-950/70 hover:border-slate-700 text-slate-400 hover:text-slate-200'
                }`}
              >
                <div className="w-8 h-8 rounded overflow-hidden bg-slate-900 shrink-0">
                  <SafeImage
                    src={preset.url}
                    fallbackCategory="general"
                    alt={preset.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-semibold truncate leading-tight">
                    {preset.badge}
                  </div>
                </div>
                {isMatch && <Check className="w-3 h-3 text-sky-400 shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Modal Dialog */}
      <ImagePickerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        currentImage={value}
        onSelectImage={onChange}
        title={`Select or Upload Image for ${label}`}
        categoryHint={categoryHint}
      />
    </div>
  );
};
